import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    OutlinedInput,
    FormControlLabel,
    Switch,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Paper,
    Snackbar
} from '@mui/material';
import { 
    Add as AddIcon, 
    Delete as DeleteIcon, 
    ExpandMore as ExpandMoreIcon 
} from '@mui/icons-material';
import FlashBetQuestionTemplateDto from '../models/dtos/FlashBetQuestionTemplateDto';
import CreateQuestionTemplateRequest from '../models/dtos/CreateQuestionTemplateRequest';
import UpdateQuestionTemplateRequest from '../models/dtos/UpdateQuestionTemplateRequest';
import AnswerDataRequest from '../models/dtos/AnswerDataRequest';
import { QuestionLogicType, QUESTION_LOGIC_OPTIONS } from '../models/dtos/QuestionLogicType';
import { createFlashBetQuestionTemplate, updateFlashBetQuestionTemplate } from '../Api';

interface QuestionTemplateModalProps {
    open: boolean;
    template: FlashBetQuestionTemplateDto | null;
    onSave: () => void;
    onClose: () => void;
}

type LocationOption = 'PRE_MATCH' | 'IN_PLAY' | 'POST_MATCH';

interface FormErrors {
    title?: string;
    logic?: string;
    answers?: string;
    locations?: string;
}

const locationOptions: LocationOption[] = ['PRE_MATCH', 'IN_PLAY', 'POST_MATCH'];

const QuestionTemplateModal: React.FC<QuestionTemplateModalProps> = ({ 
    open, template, onSave, onClose 
}) => {
    const [formData, setFormData] = useState({
        title: '',
        allowedLocations: [] as LocationOption[],
        logic: 'MATCH_WHO_WILL_WIN' as QuestionLogicType,
        parameters: {} as { [key: string]: number },
        localizations: {} as { [key: string]: string },
        answers: [] as AnswerDataRequest[],
        enabled: true
    });

    const [localizationEntries, setLocalizationEntries] = useState<Array<{ lang: string; name: string }>>([]);
    const [parameterEntries, setParameterEntries] = useState<Array<{ key: string; value: number }>>([]);
    const [errors, setErrors] = useState<FormErrors>({});
    const [saving, setSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (template) {
            setFormData({
                title: template.title,
                allowedLocations: template.allowedLocations,
                logic: template.logic,
                parameters: template.parameters || {},
                localizations: template.localizations || {},
                answers: template.answers.map(answer => ({
                    id: answer.id,
                    name: answer.name,
                    localizations: answer.localizations || {}
                })),
                enabled: template.enabled
            });

            // Convert localizations to array format for editing
            const locEntries = Object.entries(template.localizations || {}).map(([lang, name]) => ({
                lang, name
            }));
            setLocalizationEntries(locEntries);

            // Convert parameters to array format for editing
            const paramEntries = Object.entries(template.parameters || {}).map(([key, value]) => ({
                key, value
            }));
            setParameterEntries(paramEntries);
        } else {
            // Reset form for new template
            setFormData({
                title: '',
                allowedLocations: [],
                logic: 'MATCH_WHO_WILL_WIN',
                parameters: {},
                localizations: {},
                answers: [],
                enabled: true
            });
            setLocalizationEntries([]);
            setParameterEntries([]);
        }
        setErrors({});
    }, [template, open]);

    const validateForm = () => {
        const newErrors: { title?: string; logic?: string; answers?: string } = {};
        
        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }
        
        if (formData.answers.length === 0) {
            newErrors.answers = 'At least one answer is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        setSaving(true);
        try {
            // Convert arrays back to objects
            const localizations: { [key: string]: string } = {};
            localizationEntries.forEach(entry => {
                if (entry.lang.trim() && entry.name.trim()) {
                    localizations[entry.lang.trim()] = entry.name.trim();
                }
            });

            const parameters: { [key: string]: number } = {};
            parameterEntries.forEach(entry => {
                if (entry.key.trim() && !isNaN(entry.value)) {
                    parameters[entry.key.trim()] = entry.value;
                }
            });

            if (template) {
                // Update existing template
                const updateData: UpdateQuestionTemplateRequest = {
                    ...formData,
                    localizations,
                    parameters
                };
                await updateFlashBetQuestionTemplate(template.id, updateData);
            } else {
                // Create new template
                const createData: CreateQuestionTemplateRequest = {
                    title: formData.title,
                    allowedLocations: formData.allowedLocations,
                    logic: formData.logic,
                    parameters,
                    localizations,
                    answers: formData.answers
                };
                await createFlashBetQuestionTemplate(createData);
            }

            onSave();
        } catch (error) {
            console.error('Error saving template:', error);
            setErrorMessage(`Failed to ${template ? 'update' : 'create'} question template. Please try again.`);
        } finally {
            setSaving(false);
        }
    };

    const handleInputChange = (field: string) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));
        
        if (errors[field as keyof typeof errors]) {
            setErrors((prev: FormErrors) => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    const addLocalizationEntry = () => {
        setLocalizationEntries(prev => [...prev, { lang: '', name: '' }]);
    };

    const removeLocalizationEntry = (index: number) => {
        setLocalizationEntries(prev => prev.filter((_, i) => i !== index));
    };

    const updateLocalizationEntry = (index: number, field: 'lang' | 'name', value: string) => {
        setLocalizationEntries(prev => prev.map((entry, i) => 
            i === index ? { ...entry, [field]: value } : entry
        ));
    };

    const addParameterEntry = () => {
        setParameterEntries(prev => [...prev, { key: '', value: 0 }]);
    };

    const removeParameterEntry = (index: number) => {
        setParameterEntries(prev => prev.filter((_, i) => i !== index));
    };

    const updateParameterEntry = (index: number, field: 'key' | 'value', value: string | number) => {
        setParameterEntries(prev => prev.map((entry, i) => 
            i === index ? { ...entry, [field]: value } : entry
        ));
    };

    const addAnswer = () => {
        const newAnswer: AnswerDataRequest = {
            name: '',
            localizations: {}
        };
        setFormData(prev => ({
            ...prev,
            answers: [...prev.answers, newAnswer]
        }));
    };

    const removeAnswer = (index: number) => {
        setFormData(prev => ({
            ...prev,
            answers: prev.answers.filter((_, i) => i !== index)
        }));
    };

    const updateAnswer = (index: number, field: 'name', value: string) => {
        setFormData(prev => ({
            ...prev,
            answers: prev.answers.map((answer, i) => 
                i === index ? { ...answer, [field]: value } : answer
            )
        }));
    };

    const updateAnswerLocalization = (answerIndex: number, lang: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            answers: prev.answers.map((answer, i) => 
                i === answerIndex 
                    ? { 
                        ...answer, 
                        localizations: { ...answer.localizations, [lang]: value } 
                      }
                    : answer
            )
        }));
    };

    const addAnswerLocalization = (answerIndex: number) => {
        // Add a placeholder localization with empty language code
        const placeholderLang = ``;
        updateAnswerLocalization(answerIndex, placeholderLang, '');
    };

    const removeAnswerLocalization = (answerIndex: number, lang: string) => {
        setFormData(prev => ({
            ...prev,
            answers: prev.answers.map((answer, i) => {
                if (i === answerIndex) {
                    const newLocalizations = { ...answer.localizations };
                    delete newLocalizations[lang];
                    return { ...answer, localizations: newLocalizations };
                }
                return answer;
            })
        }));
    };

    const formatLogicLabel = (logic: string): string => {
        return logic.replace(/_/g, ' ').toLowerCase()
            .replace(/\b\w/g, l => l.toUpperCase());
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                {template ? 'Edit Question Template' : 'Create Question Template'}
            </DialogTitle>
            
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    <Grid container spacing={3}>
                        {/* Basic Information */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Question Title *"
                                value={formData.title}
                                onChange={handleInputChange('title')}
                                error={!!errors.title}
                                helperText={errors.title}
                                margin="normal"
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Logic Type</InputLabel>
                                <Select
                                    value={formData.logic}
                                    onChange={(e) => setFormData(prev => ({ ...prev, logic: e.target.value as QuestionLogicType }))}
                                    label="Logic Type"
                                >
                                    {QUESTION_LOGIC_OPTIONS.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {formatLogicLabel(option)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Allowed Locations</InputLabel>
                                <Select
                                    multiple
                                    value={formData.allowedLocations}
                                    onChange={(e) => setFormData(prev => ({ 
                                        ...prev, 
                                        allowedLocations: e.target.value as LocationOption[] 
                                    }))}
                                    input={<OutlinedInput label="Allowed Locations" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => (
                                                <Chip key={value} label={value.replace(/_/g, ' ')} size="small" />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {locationOptions.map((location) => (
                                        <MenuItem key={location} value={location}>
                                            {location.replace(/_/g, ' ')}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {template && (
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.enabled}
                                            onChange={(e) => setFormData(prev => ({ ...prev, enabled: e.target.checked }))}
                                        />
                                    }
                                    label="Enabled"
                                />
                            </Grid>
                        )}

                        {/* Answers Section */}
                        <Grid item xs={12}>
                            <Accordion defaultExpanded>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6">
                                        Answers ({formData.answers.length})
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {formData.answers.map((answer, index) => (
                                        <Paper key={index} sx={{ p: 2, mb: 2 }}>
                                            <Box display="flex" alignItems="center" mb={2}>
                                                <TextField
                                                    fullWidth
                                                    label={`Answer ${index + 1} (English)`}
                                                    value={answer.name}
                                                    onChange={(e) => updateAnswer(index, 'name', e.target.value)}
                                                    sx={{ mr: 1 }}
                                                />
                                                <IconButton 
                                                    color="error" 
                                                    onClick={() => removeAnswer(index)}
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                            
                                            {/* Answer Localizations */}
                                            {Object.keys(answer.localizations || {}).length > 0 && (
                                                <Box>
                                                    <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                                        Localizations for Answer {index + 1}:
                                                    </Typography>
                                                    {Object.entries(answer.localizations || {}).map(([lang, value], langIndex) => (
                                                        <Box key={`${index}-${langIndex}`} display="flex" alignItems="center" mb={1}>
                                                            <TextField
                                                                label="Language"
                                                                select
                                                                value={lang}
                                                                onChange={(e) => {
                                                                    // Handle language code change without removing/adding
                                                                    const newLang = e.target.value;
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        answers: prev.answers.map((ans, i) => {
                                                                            if (i === index) {
                                                                                const newLocalizations = { ...ans.localizations };
                                                                                // Remove old key and add new key
                                                                                delete newLocalizations[lang];
                                                                                newLocalizations[newLang] = value;
                                                                                return { ...ans, localizations: newLocalizations };
                                                                            }
                                                                            return ans;
                                                                        })
                                                                    }));
                                                                }}
                                                                sx={{ mr: 1, width: '120px' }}
                                                                size="small"
                                                            >
                                                                <MenuItem value="">Select language</MenuItem>
                                                                <MenuItem value="de">German (de)</MenuItem>
                                                                <MenuItem value="fr">French (fr)</MenuItem>
                                                                <MenuItem value="it">Italian (it)</MenuItem>
                                                                <MenuItem value="ro">Romanian (ro)</MenuItem>
                                                                <MenuItem value="ru">Russian (ru)</MenuItem>
                                                                <MenuItem value="es">Spanish (es)</MenuItem>
                                                                <MenuItem value="pt">Portuguese (pt)</MenuItem>
                                                                <MenuItem value="pl">Polish (pl)</MenuItem>
                                                                <MenuItem value="tr">Turkish (tr)</MenuItem>
                                                            </TextField>
                                                            <TextField
                                                                fullWidth
                                                                label="Localized Answer"
                                                                value={value}
                                                                onChange={(e) => updateAnswerLocalization(index, lang, e.target.value)}
                                                                sx={{ mr: 1 }}
                                                                size="small"
                                                            />
                                                            <IconButton 
                                                                color="error" 
                                                                onClick={() => removeAnswerLocalization(index, lang)}
                                                                size="small"
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}
                                            
                                            <Button
                                                size="small"
                                                variant="text"
                                                startIcon={<AddIcon />}
                                                onClick={() => addAnswerLocalization(index)}
                                                sx={{ mt: 1 }}
                                            >
                                                Add Localization for Answer {index + 1}
                                            </Button>
                                        </Paper>
                                    ))}
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={addAnswer}
                                        fullWidth
                                    >
                                        Add Answer
                                    </Button>
                                    {errors.answers && (
                                        <Typography color="error" variant="caption">
                                            {errors.answers}
                                        </Typography>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        </Grid>

                        {/* Localizations Section */}
                        <Grid item xs={12}>
                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6">
                                        Question Localizations ({localizationEntries.length})
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {localizationEntries.map((entry, index) => (
                                        <Box key={index} display="flex" alignItems="center" mb={2}>
                                            <TextField
                                                label="Language Code"
                                                select
                                                value={entry.lang}
                                                onChange={(e) => updateLocalizationEntry(index, 'lang', e.target.value)}
                                                sx={{ mr: 1, width: '120px' }}
                                            >
                                                <MenuItem value="">Select language</MenuItem>
                                                <MenuItem value="de">German (de)</MenuItem>
                                                <MenuItem value="fr">French (fr)</MenuItem>
                                                <MenuItem value="it">Italian (it)</MenuItem>
                                                <MenuItem value="ro">Romanian (ro)</MenuItem>
                                                <MenuItem value="ru">Russian (ru)</MenuItem>
                                                <MenuItem value="es">Spanish (es)</MenuItem>
                                                <MenuItem value="pt">Portuguese (pt)</MenuItem>
                                                <MenuItem value="pl">Polish (pl)</MenuItem>
                                                <MenuItem value="tr">Turkish (tr)</MenuItem>
                                            </TextField>
                                            <TextField
                                                fullWidth
                                                label="Localized Title"
                                                value={entry.name}
                                                onChange={(e) => updateLocalizationEntry(index, 'name', e.target.value)}
                                                sx={{ mr: 1 }}
                                            />
                                            <IconButton 
                                                color="error" 
                                                onClick={() => removeLocalizationEntry(index)}
                                                size="small"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    ))}
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={addLocalizationEntry}
                                        fullWidth
                                    >
                                        Add Localization
                                    </Button>
                                </AccordionDetails>
                            </Accordion>
                        </Grid>

                        {/* Parameters Section */}
                        <Grid item xs={12}>
                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6">
                                        Parameters ({parameterEntries.length})
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {parameterEntries.map((entry, index) => (
                                        <Box key={index} display="flex" alignItems="center" mb={2}>
                                            <TextField
                                                label="Parameter Key"
                                                value={entry.key}
                                                onChange={(e) => updateParameterEntry(index, 'key', e.target.value)}
                                                sx={{ mr: 1, flex: 1 }}
                                            />
                                            <TextField
                                                label="Value"
                                                type="number"
                                                value={entry.value}
                                                onChange={(e) => updateParameterEntry(index, 'value', Number(e.target.value))}
                                                sx={{ mr: 1, width: '120px' }}
                                            />
                                            <IconButton 
                                                color="error" 
                                                onClick={() => removeParameterEntry(index)}
                                                size="small"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    ))}
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={addParameterEntry}
                                        fullWidth
                                    >
                                        Add Parameter
                                    </Button>
                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button 
                    onClick={handleSave} 
                    variant="contained" 
                    disabled={saving}
                >
                    {saving ? 'Saving...' : (template ? 'Update' : 'Create')}
                </Button>
            </DialogActions>

            {/* Error Toast */}
            <Snackbar
                open={errorMessage !== null}
                autoHideDuration={4000}
                onClose={() => setErrorMessage(null)}
                message={errorMessage}
            />
        </Dialog>
    );
};

export default QuestionTemplateModal;